import { useEffect, useState } from "react";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApiError } from "../api/errors";
import AppLogo from "../components/AppLogo";
import LanguageSelector from "../components/LanguageSelector";
import { useAuth } from "../context/AuthContext";

const benefits = ["benefitProducts", "benefitConnect", "benefitAi"];

export default function Login() {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    isAuthenticated,
    initializing,
    authError,
    clearAuthError,
    contextReady,
    workspaceOperational,
    platformRole,
    legalAcceptanceRequired,
  } = useAuth();

  useEffect(() => {
    const stored = localStorage.getItem("auth_error");
    if (stored) {
      setMessage(stored);
      localStorage.removeItem("auth_error");
    }
  }, []);

  if (!initializing && isAuthenticated) {
    const destination = platformRole
      ? "/platform"
      : legalAcceptanceRequired
        ? "/legal/accept"
        : contextReady && workspaceOperational
          ? "/dashboard"
          : contextReady
            ? "/onboarding"
            : "/settings";
    return <Navigate to={destination} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!identifier.trim() || !password) return;

    setSubmitting(true);
    setMessage("");
    clearAuthError();

    try {
      const authenticatedState = await login(identifier.trim(), password);
      const requestedDestination = location.state?.from?.pathname;
      const selectedWorkspace = authenticatedState?.selection?.workspace;
      const platformDestination = authenticatedState?.user?.platform_role ? "/platform" : null;
      const storeDestination = selectedWorkspace?.effective_status === "active"
        ? "/dashboard"
        : selectedWorkspace
          ? "/onboarding"
          : "/settings";
      if (platformDestination) {
        navigate(platformDestination, { replace: true });
      } else if (authenticatedState?.legalStatus?.required) {
        navigate("/legal/accept", {
          replace: true,
          state: { from: { pathname: requestedDestination || storeDestination } },
        });
      } else {
        navigate(requestedDestination || storeDestination, { replace: true });
      }
    } catch (error) {
      setMessage(getApiError(error, t("auth.signInError")));
    } finally {
      setSubmitting(false);
    }
  };

  const successMessage = message.startsWith("Account created") || message.startsWith("Application submitted");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, sm: 3 },
        background:
          "radial-gradient(circle at 8% 12%, rgba(56,189,248,0.2), transparent 28%), radial-gradient(circle at 92% 15%, rgba(124,58,237,0.2), transparent 30%), #020617",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 980, borderRadius: 5, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 36px 100px rgba(0,0,0,.42)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.95fr 1.05fr" } }}>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: 610,
              p: 5,
              color: "white",
              background: "linear-gradient(145deg, #0f172a 0%, #172554 55%, #312e81 100%)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", bgcolor: "rgba(56,189,248,0.12)", right: -130, top: -100 }} />
            <AppLogo light />
            <Box sx={{ position: "relative" }}>
              <AutoAwesomeRoundedIcon sx={{ color: "#7dd3fc", fontSize: 38 }} />
              <Typography variant="h3" sx={{ color: "white", mt: 1.5, fontSize: "2.55rem", lineHeight: 1.08 }}>
                {t("auth.headline")}
              </Typography>
              <Typography sx={{ color: "#cbd5e1", mt: 2, lineHeight: 1.75, maxWidth: 390 }}>
                {t("auth.subtitle")}
              </Typography>
              <Stack spacing={1.4} sx={{ mt: 3.5 }}>
                {benefits.map((benefit) => (
                  <Stack key={benefit} direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: "#4ade80", fontSize: 20 }} />
                    <Typography sx={{ color: "#e2e8f0", fontWeight: 650 }}>{t(`auth.${benefit}`)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              {t("auth.secure")}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, sm: 5, md: 6 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Box sx={{ alignSelf: "flex-end", mb: 2 }}><LanguageSelector /></Box>
            <Box sx={{ display: { xs: "block", md: "none" }, mb: 4 }}>
              <AppLogo />
            </Box>
            <Typography variant="h4">{t("auth.welcome")}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75, mb: 3 }}>
              {t("auth.signInSubtitle")}
            </Typography>

            {(message || authError) && (
              <Alert severity={successMessage ? "success" : "error"} sx={{ mb: 2.25 }}>
                {message || authError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                autoFocus
                autoComplete="username"
                label={t("auth.identifier")}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                required
                autoComplete="current-password"
                label={t("auth.password")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={t(showPassword ? "auth.hidePassword" : "auth.showPassword")}
                        onClick={() => setShowPassword((current) => !current)}
                      >
                        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
                <Link to="/forgot-password" style={{ color: "#2563eb", fontWeight: 750, fontSize: 14 }}>
                  {t("auth.forgot")}
                </Link>
              </Box>
              <Button fullWidth type="submit" variant="contained" disabled={submitting} sx={{ mt: 2, py: 1.2 }}>
                {submitting ? <CircularProgress size={23} color="inherit" /> : t("auth.signIn")}
              </Button>
            </Box>

            <Typography mt={3} textAlign="center" color="text.secondary">
              {t("auth.newWorkspace")}{" "}
              <Link to="/register" style={{ color: "#2563eb", fontWeight: 850 }}>
                {t("auth.createAccount")}
              </Link>
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
              <Link to="/terms" style={{ color: "#64748b", fontWeight: 700, fontSize: 13 }}>
                {t("auth.terms")}
              </Link>
              <Link to="/privacy" style={{ color: "#64748b", fontWeight: 700, fontSize: 13 }}>
                {t("auth.privacy")}
              </Link>
            </Stack>
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
}
